// Relays events from the websocket client to other parts of app subscribed to those events

export type EventHandler<T> = (payload: T) => void;

export class EventRelay<Events extends object> {
  // Stores event listeners for each event type
  private listeners: { [K in keyof Events]?: EventHandler<Events[K]>[] } = {};

  /**
   * Subscribe to an event
   * @param event - The event name
   * @param handler - The function to call when the event is emitted
   * @returns A cleanup function to unsubscribe
   */
  on<K extends keyof Events>(
    event: K,
    handler: EventHandler<Events[K]>
  ): () => void {
    let handlers = this.listeners[event];
    if (!handlers) {
      handlers = [];
      this.listeners[event] = handlers;
    }
    handlers.push(handler);

    // Return cleanup function
    return () => this.off(event, handler);
  }

  /**
   * Unsubscribe from an event
   * @param event - The event name
   * @param handler - The function to remove
   */
  off<K extends keyof Events>(
    event: K,
    handler: EventHandler<Events[K]>
  ): void {
    const handlers = this.listeners[event];
    if (!handlers) return;
    this.listeners[event] = handlers.filter((h) => h !== handler);
  }

  /**
   * Emit an event to all subscribed handlers
   * @param event - The event name
   * @param payload - The data to pass to handlers
   */
  emit<K extends keyof Events>(event: K, payload: Events[K]): void {
    const handlers = this.listeners[event];
    if (!handlers) return;
    for (const handler of handlers) {
      handler(payload);
    }
  }
}
